import type { Rational } from './schema.ts';

/** Frames per second as a float. */
export function fpsValue(fps: Rational): number {
  return fps.num / fps.den;
}

export function framesToSeconds(frames: number, fps: Rational): number {
  return (frames * fps.den) / fps.num;
}

/** Converts seconds to the frame containing that instant (floor, with epsilon for float noise). */
export function secondsToFrame(seconds: number, fps: Rational): number {
  return Math.floor((seconds * fps.num) / fps.den + 1e-6);
}

/** Converts seconds to the nearest frame boundary. */
export function secondsToNearestFrame(seconds: number, fps: Rational): number {
  return Math.round((seconds * fps.num) / fps.den);
}

/** SMPTE-like timecode `HH:MM:SS:FF` (non-drop-frame). */
export function formatTimecode(frame: number, fps: Rational): string {
  const rate = Math.round(fpsValue(fps));
  const sign = frame < 0 ? '-' : '';
  const abs = Math.abs(frame);
  const ff = abs % rate;
  const totalSeconds = Math.floor(abs / rate);
  const ss = totalSeconds % 60;
  const mm = Math.floor(totalSeconds / 60) % 60;
  const hh = Math.floor(totalSeconds / 3600);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${sign}${p(hh)}:${p(mm)}:${p(ss)}:${p(ff)}`;
}

/** Human-friendly duration, e.g. `1:23.4`. */
export function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds - m * 60;
  return `${m}:${s.toFixed(1).padStart(4, '0')}`;
}

export const COMMON_FRAME_RATES: ReadonlyArray<{ label: string; fps: Rational }> = [
  { label: '23.976', fps: { num: 24000, den: 1001 } },
  { label: '24', fps: { num: 24, den: 1 } },
  { label: '25', fps: { num: 25, den: 1 } },
  { label: '29.97', fps: { num: 30000, den: 1001 } },
  { label: '30', fps: { num: 30, den: 1 } },
  { label: '50', fps: { num: 50, den: 1 } },
  { label: '59.94', fps: { num: 60000, den: 1001 } },
  { label: '60', fps: { num: 60, den: 1 } },
];
